/**
 * Modpack server configs — the `servers` / `servers_dev` rows in the
 * `modpack_configs` database.
 *
 * Distinct from `ServerConfig` in types/player.ts, which is the three-field
 * cluster-picker shape read from the public `api.playcdu.co/configs` endpoint.
 * This is the full row, and it is staff-only.
 */

export type ModpackConfigEnv = 'production' | 'development';

export interface ModpackConfig {
  id: number;
  server_address: string;
  modpack_name: string | null;
  modpack_full_name: string | null;
  rcon_port: number | null;
  query_port: number | null;
  server_download_url: string | null;
  ip_address: string | null;
  modpack_version: string | null;
  cluster_name: string | null;
  /** The id of the row heading this cluster. A head is self-parented. */
  cluster_parent: number | null;
  region_name: string | null;
  thumbnail_url: string | null;
  author: string | null;
  modloader: string | null;
  ftb_id: number | null;
  /** Unix seconds. Some rows carry 0 — the data predates the field. */
  date_added: number | null;
  /**
   * Unix seconds of the last world wipe, or 0/null for a pack that has never
   * been wiped. Players are shown this, so it is a record, not a status: a
   * pack wiped a year ago is simply a pack whose world is a year old.
   */
  date_wiped: number | null;
  /** The modpackindex.com id, used to join to the Apogee pack catalogue. */
  modpack_index_id: number | null;
  popularity_1d: number | null;
  popularity_7d: number | null;
  popularity_28d: number | null;
  amp_name: string | null;
  /**
   * 1 withdraws the server from every unauthenticated read of the configs API
   * and from Tesseract's LFG list; staff and the bot still see it. Distinct
   * from maintenance, which keeps a server listed.
   */
  hidden: 0 | 1;
}

export interface ModpackConfigListResponse {
  env: ModpackConfigEnv;
  total: number;
  results: ModpackConfig[];
}

export interface ModpackConfigClusterResponse {
  cluster_parent: number;
  total: number;
  results: ModpackConfig[];
}

export interface ModpackConfigCluster {
  id: number;
  name: string | null;
  members: number;
}

export interface ModpackConfigVocabularies {
  regions: string[];
  modloaders: string[];
  clusters: ModpackConfigCluster[];
}

export interface CreateModpackConfigRequest {
  server_address: string;
  modpack_name?: string | null;
  modpack_full_name?: string | null;
  rcon_port?: number | null;
  query_port?: number | null;
  server_download_url?: string | null;
  ip_address?: string | null;
  modpack_version?: string | null;
  cluster_name?: string | null;
  cluster_parent?: number | null;
  region_name?: string | null;
  thumbnail_url?: string | null;
  author?: string | null;
  modloader?: string | null;
  ftb_id?: number | null;
  modpack_index_id?: number | null;
  amp_name?: string | null;
  hidden?: 0 | 1;
}

/** Only the fields that changed — a whole-row write would clobber popularity. */
export type UpdateModpackConfigRequest = Partial<
  Omit<ModpackConfig, 'id' | 'server_address' | 'popularity_1d' | 'popularity_7d' | 'popularity_28d'>
>;

export interface UpdateModpackConfigResponse {
  success: boolean;
  changed: string[];
}

export interface CreateModpackConfigResponse {
  success: boolean;
  server_address: string;
  date_added: number;
}

export interface DeleteModpackConfigResponse {
  success: boolean;
  /** The pack promoted to head the cluster, when the deleted row headed it. */
  promoted: string | null;
  reparented: number;
}

export interface ClusterVersionBumpResponse {
  success: boolean;
  cluster_parent: number;
  modpack_version: string;
  updated: string[];
  failed: { server_address: string; error: string }[];
}

export interface ClusterWipeResponse {
  success: boolean;
  cluster_parent: number;
  /** Unix seconds recorded on every member. */
  date_wiped: number;
  updated: string[];
  failed: { server_address: string; error: string }[];
}

export interface DevSyncResponse {
  success: boolean;
  replaced: number;
  copied: number;
}

/**
 * Written by Mr. Roboto from live rankings every 100 seconds. The API refuses
 * them rather than accepting a value that reverts within two minutes.
 */
export const BOT_OWNED_FIELDS = [
  'popularity_1d',
  'popularity_7d',
  'popularity_28d',
] as const;

/**
 * Network and identity fields. Getting one wrong takes a server off the
 * network or points restarts at the wrong AMP instance, so the backend gates
 * them on `argus.modpackconfigs.admin`.
 */
export const ADMIN_ONLY_FIELDS = [
  'ip_address',
  'rcon_port',
  'query_port',
  'amp_name',
  'date_added',
] as const;

/** The sentinel `cluster_name` for a pack that belongs to no cluster. */
export const UNCLUSTERED = 'default';

// --- The Discord side, reached through Mr. Roboto --------------------------

export interface DiscordCategory {
  id: string;
  name: string;
}

export interface DiscordForum extends DiscordCategory {
  category_id: string | null;
}

export interface DiscordTargets {
  categories: DiscordCategory[];
  forums: DiscordForum[];
}

export interface ProvisionResult {
  modpack_id: number | null;
  channel_id: string | null;
  channel_created: boolean;
}

export interface AnnounceUpdateResult {
  announced: boolean;
  points_awarded: boolean;
  notes_written: number;
}

export interface AnnounceLaunchResult {
  announced: boolean;
  message_id: string | null;
}

export interface ChangeClusterHeadResponse {
  success: boolean;
  previous_head: number;
  new_head: number;
  reparented: number;
}
